/**
 * WorkspacePage — standalone /workspace demo page.
 *
 * Not under /tenants/. Independent of the voice sub-tenant configs.
 * Connects to the workspace runtime (ws://localhost:3009/workspace-ws)
 * to run a voice-driven, HTML-rendered session.
 *
 * v1:
 *   - Tenant/sub-tenant pickers are cosmetic (attached to the session
 *     only as labels, don't affect gateway behavior yet — the workspace
 *     runtime is currently form-filler-only with a hardcoded schema).
 *   - One workflow available: referral-intake.
 *   - Canvas shows whichever view the agent has rendered.
 *   - Transcript bar shows live STT/TTS.
 *   - Keyboard + voice are peers; typing a field emits a client_action
 *     that the agent sees as a user input event.
 */

import React, { useEffect, useState } from "react";
import * as api from "../api.js";
import Canvas from "../workspace/Canvas.jsx";
import useWorkspaceSession from "../workspace/useWorkspaceSession.js";

const WORKFLOWS = [
  { id: "referral-intake", label: "New Patient Referral" },
];

const MODEL_OPTIONS = [
  { value: "gemini-2.5-flash",      label: "Gemini 2.5 Flash (balanced)" },
  { value: "gemini-2.5-flash-lite", label: "Gemini 2.5 Flash Lite (fastest)" },
  { value: "gemini-2.5-pro",        label: "Gemini 2.5 Pro (most accurate)" },
  { value: "gpt-4o-mini",           label: "GPT-4o Mini" },
];

export default function WorkspacePage() {
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [subTenants, setSubTenants] = useState([]);
  const [subTenantId, setSubTenantId] = useState("");
  const [workflow, setWorkflow] = useState("referral-intake");
  const [textModel, setTextModel] = useState("gemini-2.5-flash");
  const [sttProvider, setSttProvider] = useState("google");
  const [ttsProvider, setTtsProvider] = useState("google");

  const session = useWorkspaceSession();

  // Load tenants for the picker (cosmetic; used for display + passed
  // to the session as labels but doesn't drive behavior in v1).
  useEffect(() => {
    api.listTenants().then((res) => {
      const list = res.data || [];
      setTenants(list);
      if (list.length > 0 && !tenantId) setTenantId(list[0]._id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!tenantId) { setSubTenants([]); setSubTenantId(""); return; }
    api.listSubTenants(tenantId).then((res) => {
      const list = res.data || [];
      setSubTenants(list);
      if (list.length > 0) setSubTenantId(list[0]._id);
      else setSubTenantId("");
    }).catch(() => { setSubTenants([]); setSubTenantId(""); });
  }, [tenantId]);

  const canStart = session.status === "idle" || session.status === "ended" || session.status === "error";
  const isLive   = session.status === "live" || session.status === "ready";

  const onStart = () => {
    const tenant = tenants.find((t) => t._id === tenantId);
    const sub    = subTenants.find((s) => s._id === subTenantId);
    session.start({
      workflow,
      tenantId,
      subTenantId,
      textModel,
      stt_provider: sttProvider,
      tts_provider: ttsProvider,
      practiceName: tenant?.name || sub?.name || "the clinic",
    });
  };

  // Action handler — receives events from the rendered view.
  // e.g. the ReferralForm emits { type: "field_changed", field_id, value }
  // or { type: "submit_form_click" } / { type: "clear_form_click" }.
  const onAction = (action) => {
    if (!action) return;
    if (action.type === "clear_form_click") {
      // Local-only reset: clear the referral_form view's values.
      session.sendAction({ type: "form_cleared" });
      return;
    }
    session.sendAction(action);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: "#fafbfc" }}>
      {/* ── Top control bar ──────────────────────────────── */}
      <div style={{
        padding: "10px 20px",
        borderBottom: "1px solid var(--color-border, #e0e0e0)",
        background: "#fff",
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
      }}>
        <div style={{ fontWeight: 600, fontSize: 15, marginRight: 12 }}>Workspace</div>

        <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} disabled={isLive} style={inputStyle}>
          {tenants.length === 0 && <option value="">(no tenants)</option>}
          {tenants.map((t) => <option key={t._id} value={t._id}>{t.name}</option>)}
        </select>

        <select value={subTenantId} onChange={(e) => setSubTenantId(e.target.value)} disabled={isLive} style={inputStyle}>
          {subTenants.length === 0 && <option value="">(no sub-tenants)</option>}
          {subTenants.map((s) => <option key={s._id} value={s._id}>{s.name || s._id}</option>)}
        </select>

        <select value={workflow} onChange={(e) => setWorkflow(e.target.value)} disabled={isLive} style={inputStyle}>
          {WORKFLOWS.map((w) => <option key={w.id} value={w.id}>{w.label}</option>)}
        </select>

        <select value={textModel} onChange={(e) => setTextModel(e.target.value)} disabled={isLive} style={inputStyle}
                title="LLM model for the agent">
          {MODEL_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>

        <div style={{ display: "flex", gap: 6, alignItems: "center" }} title="Speech provider">
          <select value={sttProvider} onChange={(e) => setSttProvider(e.target.value)} disabled={isLive} style={{ ...inputStyle, minWidth: 90 }}>
            <option value="google">STT: Google</option>
            <option value="cartesia">STT: Cartesia</option>
          </select>
          <select value={ttsProvider} onChange={(e) => setTtsProvider(e.target.value)} disabled={isLive} style={{ ...inputStyle, minWidth: 90 }}>
            <option value="google">TTS: Google</option>
            <option value="cartesia">TTS: Cartesia</option>
          </select>
        </div>

        <div style={{ flex: 1 }} />

        <StatusPill status={session.status} />

        {canStart ? (
          <button className="btn primary" onClick={onStart} disabled={!workflow}>
            🎙 Start
          </button>
        ) : (
          <button className="btn" onClick={session.stop}>
            ■ Stop
          </button>
        )}
      </div>

      {/* ── Transcript (thin bar under the top control) ──────── */}
      <TranscriptBar transcript={session.transcript} error={session.error} />

      {/* ── Canvas (takes the rest of the screen) ───────────── */}
      <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
        <Canvas views={session.views} onAction={onAction} />
      </div>
    </div>
  );
}

const inputStyle = {
  padding: "4px 8px",
  fontSize: 13,
  border: "1px solid var(--color-border, #ccc)",
  borderRadius: 4,
  background: "#fff",
  minWidth: 140,
};

function StatusPill({ status }) {
  const map = {
    idle:       { label: "idle",       color: "#999",    bg: "#f5f5f5" },
    connecting: { label: "connecting…", color: "#1976d2", bg: "#e3f2fd" },
    ready:      { label: "ready",      color: "#1976d2", bg: "#e3f2fd" },
    live:       { label: "● live",     color: "#2e7d32", bg: "#e8f5e9" },
    ended:      { label: "ended",      color: "#757575", bg: "#eeeeee" },
    error:      { label: "error",      color: "#c62828", bg: "#ffebee" },
  };
  const s = map[status] || map.idle;
  return (
    <span style={{
      padding: "3px 10px", fontSize: 12, fontWeight: 600,
      color: s.color, background: s.bg, borderRadius: 10,
    }}>{s.label}</span>
  );
}

function TranscriptBar({ transcript, error }) {
  const recent = transcript.slice(-3);
  const text = recent.length === 0
    ? (error ? <span style={{ color: "#c62828" }}>Error: {error}</span> : <span style={{ color: "#999" }}>(no transcript yet)</span>)
    : recent.map((t, i) => (
        <span key={i} style={{ marginRight: 12 }}>
          <span style={{
            fontSize: 10, fontWeight: 700, textTransform: "uppercase",
            color: t.role === "agent" ? "#1976d2" : t.role === "patient" ? "#2e7d32" : "#999",
            marginRight: 4,
          }}>{t.role}</span>
          <span style={{ color: "#333" }}>{t.text}</span>
        </span>
      ));
  return (
    <div style={{
      padding: "6px 20px",
      fontSize: 12, background: "#fff8e1",
      borderBottom: "1px solid var(--color-border, #e0e0e0)",
      minHeight: 28,
      overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
    }}>
      {text}
    </div>
  );
}
